import React, { useEffect, useRef } from 'react'
import qrHeroImg from './images/qr_heroImg.webp';

const QR_HeroSection = () => {
    const sectionRef = useRef(null);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    el.classList.add('in-view'); // trigger fade-up
                    io.unobserve(el);
                }
            },
            { threshold: 0.2 }
        );

        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="position-relative ws-reveal"
            style={{
                overflow: "hidden",
                background: `
          linear-gradient(to bottom, rgba(255,255,255,0) 78%, rgba(255,255,255,1) 100%),
          radial-gradient(900px 600px at 0% 0%, rgba(204, 233, 254, 0.35) 0%, rgba(191,228,255,0) 60%),
          radial-gradient(900px 600px at 100% 0%, rgba(215, 255, 238, 0.28) 0%, rgba(192,252,226,0) 60%),
          linear-gradient(135deg, #f0f5ffff 0%, #EFFFF7 100%)
        `,
            }}
        >
            {/* wrapper aligned to Header container */}
            <div
                className="mx-auto position-relative ws-wrap"
                style={{
                    maxWidth: "74%",
                }}
            >
                <div
                    className="ws-hero"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 24,
                        alignItems: "stretch",
                    }}
                >
                    {/* LEFT */}
                    <header
                        className="fade-up"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <h2
                            style={{
                                fontSize: "2.7rem",
                                fontFamily: "Inter",
                                fontWeight: 650,
                                color: "#242121ff",
                                lineHeight: 1,
                            }}
                        >
                            <span style={{ display: "block" }}>
                               WhatsApp QR Code Generator: Create Instant Chat QR Codes for Free
                            </span>
                        </h2>

                        <p
                            style={{
                                marginTop: 6,
                                color: "#413e3e",
                                fontSize: "1.1rem",
                                lineHeight: 1.2,
                                fontFamily: "Poppins",
                                fontWeight: 300,
                                maxWidth: 780,
                            }}
                        >
                            Make engagement problems a thing of the past with a WhatsApp QR Code Generator. Let your customers (current and potential) reach you effortlessly, capturing every possible opportunity.
                        </p>

                        {/* CTAs buttons */}
                        <div
                            className="d-flex gap-3 flex-wrap"
                            style={{
                                display: "flex",
                                gap: 12,
                                marginTop: 10,
                                flexWrap: "wrap",
                            }}
                        >
                            <a
                                href="#demo"
                                aria-label="Start free demo"
                                className="cta-primary"
                            >
                                <span className="cta-text">Generate Links Now</span>                             
                            </a>
                        </div>
                    </header>

                    {/* RIGHT */}
                    <div
                        className="ws-hero__img fade-right"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: "20px",
                            marginBottom: "80px",
                        }}
                    >
                        <div
                            style={{
                                width: "624px",
                                height: "500px",
                                borderRadius: "18px",
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={qrHeroImg}
                                alt="WhatsApp QR preview"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* responsive + animation styles */}
            <style>
                {`
        .ws-wrap{ padding-left:16px; padding-right:16px; }
        @media (min-width: 768px){
          .ws-wrap{ padding-left:24px; padding-right:24px; }
        }

        @media (max-width: 1024px){
          .ws-hero{ grid-template-columns: 1fr; }
          .ws-hero__img{ margin-top: 8px; }
        }
        @media (max-width: 640px){
          .ws-hero h1{
            font-size: 40px !important;
            line-height: 1.08 !important;
          }
        }

        /* reveal on scroll */
        .ws-reveal{
          opacity: 0;
          transform: translateY(48px);
          transition: opacity .85s ease, transform .85s ease;
          will-change: opacity, transform;
        }
        .ws-reveal.in-view{
          opacity: 1;
          transform: translateY(0);
        }

        /* Animations */
        @keyframes fadeUp {
          from {opacity:0; transform:translateY(40px);}
          to {opacity:1; transform:translateY(0);}
        }
        @keyframes fadeRight {
          from {opacity:0; transform:translateX(60px);}
          to {opacity:1; transform:translateX(0);}
        }

        .fade-up { animation: fadeUp 0.9s ease-out both; }
        .fade-right { animation: fadeRight 0.9s ease-out both; }

        /* REQUIRED: the spinning animation (global once) */
        @keyframes spin{
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .features-list{
          display:flex;
          flex-direction: column;
          flex-wrap:wrap;
          gap:14px 18px;
          margin-top:12px;
        }
        .feature-item{
          display:flex;
          align-items:center;
          gap:10px;
        }
        .icon-badge{
          width:28px; height:28px;
          border-radius:999px;
          display:inline-flex; align-items:center; justify-content:center;
          background: rgba(29,185,84,0.14);   /* light green circle */
        }
        .icon-badge .icon{
          font-size:18px;
          color:#1DB954;                      /* green check */
        }
        .feature-text{
          font-family: Poppins;
          font-size:1.1rem;
          font-weight:300;
          color:#413e3e;                      /* dark blue text */
          line-height:1.25;
        }

        /* cta buttons (start a demo) */
        .cta-primary{
          position: relative;
          /* grid trick: center text; keep an arrow slot */
          display: grid;
          grid-template-columns: 1fr auto 26px;
          align-items: center;
          justify-items: center;
          padding: 14px 16px;
          border-radius: 10px;
          background: #054E91;
          color: #f4f2f2ff;
          text-decoration: none;
          font-family: Poppins;
          font-size: 18px;
          font-weight: 500;
          line-height: 1.2;
          cursor: pointer;
        }
        .cta-primary:hover{
          background: #075aa8;
        }
        .cta-primary .cta-text{
          grid-column: 2;
          transition: transform .45s ease, opacity .45s ease;
        }
        
        .metric-row{
          display:flex;
          gap:16px;
          flex-wrap:nowrap;        
          overflow-x:auto;           
          padding-bottom:2px;
          -webkit-overflow-scrolling:touch;
          scrollbar-width:none;
        }
        .metric-row::-webkit-scrollbar{ display:none; }

        .metric-card{
          margin-top: 2.7rem;
          border-radius:14px;
          background: rgba(234,244,255,.55);
          border:1px solid rgba(188,215,255,.60);
          padding: 18px 18px;
        }

        .metric-text{
          color:#0A2C54; /* dark blue */
          font:600 16px/1.3 Poppins, Inter, system-ui;
        }

        .meter{
          height:8px;
          margin-top:10px;
          background:rgba(10,86,162,.18); /* track */
          border-radius:999px;
          overflow:hidden;
        }
        .meter .fill{
          display:block;
          height:100%;
          width:var(--fill, 80%); /* inline se set */
          background:linear-gradient(90deg,#1DB954,#18c76a); /* green fill */
          border-radius:inherit;
        }

        /* === GLOBAL OVERRIDE: remove ALL box-shadows in this section === */
        .ws-reveal, .ws-reveal * {
          box-shadow: none !important;
        }
      `}
            </style>
        </section>
    );
};

export default QR_HeroSection